
import JigTextField from '@/components/input/JigTextField.vue'
import JigTooltip from '@/components/input/JigTooltip.vue'
import PromptYesNo from '@/components/modals/PromptYesNo.vue'
import CalloutBanner from '@/components/share/CalloutBanner.vue'
import { TenantUser } from '@/store/modules/app/types'
import {
  CreateSecurityCertificatePayload,
  ExistingSecurityCertificate,
  NewSecurityCertificate,
} from '@/store/modules/teamlibrary/types'
import { Namespace } from '@/store/types'
import { Filters } from '@/utils/filters'
import { TenantHelpers } from '@/utils/tenant-helpers'
import IconBin from '@svg/IconBin.svg'
import IconChevronDown from '@svg/IconChevronDown.svg'
import IconClose from '@svg/IconClose.svg'
import IconCopy from '@svg/IconCopy.svg'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { Action, Getter, State } from 'vuex-class'

declare var consoleError: any

/**
 * Modal for managing TOL security certificates.
 * Allows users to generate new certificate for selected tenant users,
 * view existing keys, copy key values, and delete keys.
 */
@Component({
  components: {
    IconBin,
    IconChevronDown,
    IconClose,
    IconCopy,
    JigTextField,
    JigTooltip,
    PromptYesNo,
    CalloutBanner,
  },
})
export default class LibraryCertificatesModalVue extends Vue {
  // ──────────────────────────────────────────────────────────────────────────────
  // Props
  // ──────────────────────────────────────────────────────────────────────────────

  @Prop({ type: Boolean, default: false })
  public value!: boolean

  // ──────────────────────────────────────────────────────────────────────────────
  // Vuex State / Getters / Actions
  // ──────────────────────────────────────────────────────────────────────────────

  @State('tenantUsers', { namespace: Namespace.App })
  public tenantUsers!: TenantUser[]

  @State('securityCertificates', { namespace: Namespace.TeamLibrary })
  public securityCertificates!: ExistingSecurityCertificate[]

  @Action('archiveSecurityCertificate', { namespace: Namespace.TeamLibrary })
  public archiveSecurityCertificate: any
  @Action('createSecurityCertificate', { namespace: Namespace.TeamLibrary })
  public createSecurityCertificate: any
  @Action('loadSecurityCertificates', { namespace: Namespace.TeamLibrary })
  public loadSecurityCertificates: any

  // ──────────────────────────────────────────────────────────────────────────────
  // Data
  // ──────────────────────────────────────────────────────────────────────────────

  private isLoading: boolean = false
  private isSubmitting: boolean = false
  private selectedUser: TenantUser | null = null
  private validateOnGenerate: boolean = false
  private copiedBase64Certificate: string = ''
  private newCertificate: NewSecurityCertificate | null = null
  private showCopyWarning: boolean = false
  private certificateToArchive: ExistingSecurityCertificate | null = null
  private errorMessage: string = ''
  private isPermissionError: boolean = false

  declare $refs: Vue['$refs'] & {
    deleteCertificatePromptYesNo: PromptYesNo
    certificateForm: HTMLFormElement & { validate: () => boolean; reset: () => void }
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Computed
  // ──────────────────────────────────────────────────────────────────────────────

  private get userSelectRules(): Array<(v: any) => boolean | string> {
    if (!this.validateOnGenerate) return []
    return [(v: any) => !!v || false]
  }

  private get isVisible(): boolean {
    return this.value
  }

  private set isVisible(val: boolean) {
    this.$emit('input', val)
  }

  private get hasError(): boolean {
    return this.errorMessage.length > 0
  }

  /**
   * Enriches security certificates with user_name and user_id
   * by matching user_email against valid tenant users.
   */
  private get certificatesArray(): ExistingSecurityCertificate[] {
    if (this.isLoading) return []

    const users = this.validTenantUsers
    return this.securityCertificates.map((cert: ExistingSecurityCertificate) => {
      const matchedUser = users.find((u: TenantUser) => u.email === cert.user_email)
      return Object.assign({}, cert, {
        user_name: matchedUser ? matchedUser.name : cert.user_email,
        user_id: matchedUser ? matchedUser.Uid : -1,
      })
    })
  }

  /**
   * Filters tenant users to exclude those with Presenter or Guest Viewer roles.
   */
  private get validTenantUsers(): TenantUser[] {
    if (!this.tenantUsers) {
      return []
    }

    return this.tenantUsers.filter((user: TenantUser) => {
      const hasPresenterRole = user.roles.includes(TenantHelpers.rolePresenter1)
      const hasGuestViewerRole = user.roles.includes(TenantHelpers.roleGuestViewer1)
      return !hasPresenterRole && !hasGuestViewerRole
    })
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Watchers
  // ──────────────────────────────────────────────────────────────────────────────

  @Watch('value')
  private onValueChanged(newVal: boolean) {
    if (newVal) {
      this.fetchCertificates()
    }
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Methods
  // ──────────────────────────────────────────────────────────────────────────────

  private async fetchCertificates() {
    this.errorMessage = ''
    this.isPermissionError = false
    this.isLoading = true

    const result = await this.loadSecurityCertificates()
    if (!result.success) {
      this.errorMessage = result.error
    }
    this.isLoading = false
  }

  private closeDialog() {
    this.isVisible = false
    this.newCertificate = null
    this.showCopyWarning = false
    this.errorMessage = ''
    this.isPermissionError = false
    this.validateOnGenerate = false

    if (this.$refs.certificateForm) {
      this.$refs.certificateForm.reset()
    }

    this.selectedUser = null
  }

  private formattedDate(datetime: string) {
    return Filters.formatDate(datetime, undefined, true)
  }

  private displayCertificateValue(certificate: ExistingSecurityCertificate): string {
    const previewAppendix: string = '************'
    if (!certificate.base64_certificate && !certificate.partial_certificate) return previewAppendix

    let key: string = ''
    if (!certificate.base64_certificate && certificate.partial_certificate) {
      key = certificate.partial_certificate
    } else if (certificate.base64_certificate) {
      key = certificate.base64_certificate.slice(0, 13)
    }

    return `${key}${previewAppendix}`
  }

  private onUserSelected() {
    this.validateOnGenerate = false
  }

  private async generateCertificate() {
    this.validateOnGenerate = true
    if (!this.$refs.certificateForm.validate()) {
      return
    }

    this.errorMessage = ''
    this.isPermissionError = false
    this.newCertificate = null
    this.isSubmitting = true

    const createCertificatePayload: CreateSecurityCertificatePayload = {
      auth_name: 'dashboard_tol_api',
      auth_type: 'ECDSA256',
      audience: 'dashboard',
      issued_at: new Date().toISOString(),
      expires_at: '2099-12-31T00:00:00.000000+00:00',
      permissions: ['library:create'],
      user_email: this.selectedUser!.email,
      user_name: this.selectedUser!.name,
    }
    const createResult = await this.createSecurityCertificate(createCertificatePayload)

    if (!createResult.success) {
      if (createResult.error.toLowerCase().startsWith('user does not have permission:')) {
        this.isPermissionError = true
        this.errorMessage = 'API keys can only be generated for Creator or Team Admin roles.'
      } else {
        this.errorMessage = createResult.error
      }
      this.isSubmitting = false
      return
    }

    this.newCertificate = createResult.data

    const loadResult = await this.loadSecurityCertificates()
    if (!loadResult.success) {
      this.errorMessage = loadResult.error
    }

    if (this.newCertificate != null) {
      for (const certificate of this.certificatesArray) {
        if (certificate.id !== this.newCertificate.auth_id) {
          continue
        }

        certificate.base64_certificate = this.newCertificate.base64_certificate
      }
    }

    this.validateOnGenerate = false
    this.selectedUser = null
    this.showCopyWarning = true
    this.isSubmitting = false
  }

  private showDeleteCertificatePrompt(certificate: ExistingSecurityCertificate) {
    this.certificateToArchive = certificate
    this.$refs.deleteCertificatePromptYesNo.ShowPrompt()
  }

  private closeDeleteModal() {
    if (this.$refs.deleteCertificatePromptYesNo) {
      this.$refs.deleteCertificatePromptYesNo.ClosePrompt()
    }
  }

  private async deleteCertificateById() {
    if (!this.certificateToArchive) return

    this.errorMessage = ''
    this.isPermissionError = false
    this.isSubmitting = true

    const archiveResult = await this.archiveSecurityCertificate(this.certificateToArchive.id)

    if (!archiveResult.success) {
      this.errorMessage = archiveResult.error
      this.isSubmitting = false
      return
    }

    const loadResult = await this.loadSecurityCertificates()
    if (!loadResult.success) {
      this.errorMessage = loadResult.error
    }
    this.isSubmitting = false
  }

  private copyCertificate(certificate: ExistingSecurityCertificate) {
    if (certificate == null || certificate.base64_certificate == null) {
      return
    }

    window.navigator.clipboard.writeText(certificate.base64_certificate).then(
      () => {
        if (certificate == null || certificate.base64_certificate == null) {
          return
        }

        this.copiedBase64Certificate = certificate.base64_certificate

        setTimeout(() => {
          this.copiedBase64Certificate = ''
        }, 2000)
      },
      () => {
        // clipboard write failed
        consoleError('Failed to copy API key to clipboard')
      }
    )
  }
}
